import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  createContext,
  useContext,
  Fragment,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { formatUsageWithCost, useCostDisplay } from "../lib/cost.ts";
import { CostEstimate } from "../components/CostEstimate.tsx";
import { ImageGallery } from "../components/ImageGallery.tsx";
import { PaperTables, usePaperTables } from "../components/PaperTables.tsx";
import { useUnsavedChanges } from "../lib/useUnsavedChanges.tsx";
import { SaveControls } from "../components/SaveControls.tsx";
import {
  Bot,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  Quote,
  Highlighter,
  Trash2,
  Plus,
  X,
  FileText,
  Crosshair,
  ImageIcon,
  Table2,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExtractionResponse {
  extraction: {
    id: string;
    data: Record<string, unknown>;
    isAiSuggested: boolean;
    isConfirmed: boolean;
    version: number;
    notes: string | null;
  } | null;
}

interface StudyResponse {
  study: {
    id: string;
    title: string;
    abstract: string | null;
    year: number | null;
    authors: string[];
    studyReviews: { review: { id: string; name: string; type: string } }[];
  };
}

type PaperSource = "pdf" | "abstract" | "scanned_pdf" | "none";

interface PaperTextResponse {
  text: string;
  source: PaperSource;
}

type HighlightPurpose = "EVIDENCE" | "QUOTE_FOR_REVIEW" | "NOTE";

interface Highlight {
  id: string;
  fieldKey: string | null;
  quote: string;
  startOffset: number | null;
  endOffset: number | null;
  note: string | null;
  purpose: HighlightPurpose;
  isAiGenerated: boolean;
  /** FIGURE evidence was read off an image rather than quoted from the text. */
  sourceKind?: "TEXT" | "FIGURE";
  sourcePage?: number | null;
  /** Set when the evidence came from an image analysed in the gallery. */
  imageId?: string | null;
  createdBy?: { id: string; name: string } | null;
}

interface Topic {
  id: string;
  key: string;
  label: string;
  description: string | null;
  createdBy?: { id: string; name: string } | null;
}

// ─── Field labels (for the assign-to-field dropdown and provenance display) ──

const SHARED_FIELD_LABELS: Record<string, string> = {
  diagnoses: "Diagnoses",
  sampleSize: "Sample size",
  ageRangeMin: "Age range (min)",
  ageRangeMax: "Age range (max)",
  country: "Country",
  participantType: "Participant type",
  studyDesign: "Study design",
  xrModality: "XR modality",
  deviceName: "Device name",
  deviceManufacturer: "Manufacturer",
  deliveryMode: "Delivery mode",
  eyeTrackingIntegrated: "Eye-tracking integrated",
  neglectTerminologyUsed: "Neglect terminology",
  fundingSource: "Funding source",
};

const TYPE_FIELD_LABELS: Record<string, Record<string, string>> = {
  ASSESSMENT: {
    assessmentType: "Assessment type",
    comparisonCondition: "Comparison condition",
    comparisonDetail: "Comparison detail",
    validityEvidence: "Validity evidence",
    reliabilityMetrics: "Reliability metrics",
    performanceMeasures: "Performance measures",
    dropoutRate: "Dropout rate",
    adverseEventsReported: "Adverse events reported",
    tolerabilityNotes: "Tolerability notes",
    usabilityRating: "Usability rating",
  },
  TREATMENT: {
    interventionDescription: "Intervention description",
    vrTaskType: "VR task type",
    durationPerSession: "Duration per session",
    sessionsPerWeek: "Sessions per week",
    totalSessions: "Total sessions",
    comparisonCondition: "Comparison condition",
    primaryOutcomeMeasure: "Primary outcome measure",
    effectSize: "Effect size",
    pValue: "p-value",
    robAssessmentType: "Risk of bias tool",
    robOverallJudgement: "RoB judgement",
    robNotes: "RoB notes",
  },
  TECHNICAL: {
    softwareType: "Software type",
    softwareName: "Software name",
    interactionModalities: "Interaction modalities",
    feedbackTypes: "Feedback types",
    outcomeMeasuresCaptured: "Outcome measures captured",
  },
};

function fieldLabel(key: string, reviewType: string | undefined, topics: Topic[]): string {
  const topic = topics.find((t) => t.key === key);
  if (topic) return topic.label;
  return (
    SHARED_FIELD_LABELS[key] ??
    (reviewType ? TYPE_FIELD_LABELS[reviewType]?.[key] : undefined) ??
    key
  );
}

// ─── Provenance context (links form fields to highlights in the text pane) ───

interface ProvenanceContextValue {
  byField: Record<string, Highlight[]>;
  jumpToHighlight: (h: Highlight) => void;
  registerField: (key: string, el: HTMLElement | null) => void;
  activeFieldKey: string | null;
}

const ProvenanceContext = createContext<ProvenanceContextValue | null>(null);

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ExtractionPage() {
  const { studyId, reviewId } = useParams<{ studyId: string; reviewId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [notes, setNotes] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiWarning, setAiWarning] = useState<string | null>(null);
  const [aiUsageLine, setAiUsageLine] = useState<string | null>(null);
  // What was last loaded from or saved to the server; the form is "dirty" when it differs.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [saving, setSaving] = useState<"draft" | "confirm" | "unconfirm" | null>(null);
  const [savedThisVisit, setSavedThisVisit] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeHighlightId, setActiveHighlightId] = useState<string | null>(null);
  const [activeFieldKey, setActiveFieldKey] = useState<string | null>(null);
  const [leftTab, setLeftTab] = useState<"text" | "images" | "tables">("text");
  const [openImageId, setOpenImageId] = useState<string | null>(null);
  const [tableFocus, setTableFocus] = useState<{ label: string; at: number } | null>(null);
  const costDisplay = useCostDisplay();

  const highlightElRefs = useRef(new Map<string, HTMLElement>());
  const fieldElRefs = useRef(new Map<string, HTMLElement>());
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: studyData } = useQuery({
    queryKey: ["study", studyId],
    queryFn: () => api.get<StudyResponse>(`/studies/${studyId ?? ""}`),
    enabled: !!studyId,
  });

  const review = studyData?.study.studyReviews.find((sr) => sr.review.id === reviewId)?.review;

  const { data: extractionData, isLoading } = useQuery({
    queryKey: ["extraction", studyId, reviewId],
    queryFn: () =>
      api.get<ExtractionResponse>(`/extraction/${studyId ?? ""}/${reviewId ?? ""}`),
    enabled: !!studyId && !!reviewId,
  });

  const { data: paperData } = useQuery({
    queryKey: ["paper-text", studyId],
    queryFn: () => api.get<PaperTextResponse>(`/extraction/${studyId ?? ""}/text`),
    enabled: !!studyId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetched with the page (not only when the Tables tab opens) so the paper
  // text can mark where each table starts.
  const { data: tablesData } = usePaperTables(studyId, paperData?.source === "pdf");
  const paperTables = useMemo(() => tablesData?.tables ?? [], [tablesData]);
  const tableMarks = useMemo(
    () => paperTables.filter((t) => t.textOffset !== null).map((t) => ({ offset: t.textOffset as number, label: t.label })),
    [paperTables]
  );

  const { data: highlightsData } = useQuery({
    queryKey: ["highlights", studyId, reviewId],
    queryFn: () =>
      api.get<{ highlights: Highlight[] }>(
        `/extraction/${studyId ?? ""}/${reviewId ?? ""}/highlights`
      ),
    enabled: !!studyId && !!reviewId,
  });

  const { data: topicsData } = useQuery({
    queryKey: ["topics", reviewId],
    queryFn: () => api.get<{ topics: Topic[] }>(`/extraction/topics/${reviewId ?? ""}`),
    enabled: !!reviewId,
  });

  const highlights = useMemo(() => highlightsData?.highlights ?? [], [highlightsData]);
  const topics = useMemo(() => topicsData?.topics ?? [], [topicsData]);

  const byField = useMemo(() => {
    const map: Record<string, Highlight[]> = {};
    for (const h of highlights) {
      if (!h.fieldKey) continue;
      (map[h.fieldKey] ??= []).push(h);
    }
    return map;
  }, [highlights]);

  useEffect(() => {
    if (extractionData?.extraction?.data) {
      const data = extractionData.extraction.data as Record<string, unknown>;
      const n = extractionData.extraction.notes ?? "";
      setFormData(data);
      setNotes(n);
      setSavedSnapshot(JSON.stringify({ data, notes: n }));
    } else if (review) {
      const data = { reviewType: review.type };
      setFormData(data);
      setSavedSnapshot(JSON.stringify({ data, notes: "" }));
    }
  }, [extractionData, review]);

  const currentSnapshot = JSON.stringify({ data: formData, notes });
  const dirty = savedSnapshot !== null && currentSnapshot !== savedSnapshot;
  const { guard, dialog: leaveDialog } = useUnsavedChanges(dirty);

  const handleSave = async (confirm: boolean) => {
    setSaving(confirm ? "confirm" : "draft");
    setSaveError(null);
    try {
      await api.put(`/extraction/${studyId ?? ""}/${reviewId ?? ""}`, { data: formData, notes, confirm });
      setSavedSnapshot(currentSnapshot);
      setSavedThisVisit(true);
      await qc.invalidateQueries({ queryKey: ["extraction", studyId, reviewId] });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Saving failed. Your changes are still on the page.");
    } finally {
      setSaving(null);
    }
  };

  const handleUnconfirm = async () => {
    setSaving("unconfirm");
    setSaveError(null);
    try {
      await api.post(`/extraction/${studyId ?? ""}/${reviewId ?? ""}/unconfirm`, {});
      await qc.invalidateQueries({ queryKey: ["extraction", studyId, reviewId] });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not mark as not confirmed.");
    } finally {
      setSaving(null);
    }
  };

  const createHighlightMutation = useMutation({
    mutationFn: (payload: {
      fieldKey: string | null;
      quote: string;
      startOffset: number;
      endOffset: number;
      note: string | null;
      purpose: HighlightPurpose;
    }) => api.post(`/extraction/${studyId ?? ""}/${reviewId ?? ""}/highlights`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["highlights", studyId, reviewId] }),
  });

  const deleteHighlightMutation = useMutation({
    mutationFn: (highlightId: string) => api.delete(`/extraction/highlights/${highlightId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["highlights", studyId, reviewId] }),
  });

  const createTopicMutation = useMutation({
    mutationFn: (payload: { label: string; description: string | null }) =>
      api.post(`/extraction/topics/${reviewId ?? ""}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["topics", reviewId] }),
  });

  const deleteTopicMutation = useMutation({
    mutationFn: (topicId: string) =>
      api.delete(`/extraction/topics/${reviewId ?? ""}/${topicId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["topics", reviewId] }),
  });

  const handleAiPrefill = async () => {
    setAiLoading(true);
    setAiWarning(null);
    try {
      const result = await api.post<{
        suggestion: Record<string, unknown>;
        highlights: Highlight[];
        warning: string;
        modelId?: string | null;
        usage?: { inputTokens: number; outputTokens: number } | null;
      }>(`/extraction/${studyId ?? ""}/${reviewId ?? ""}/ai-prefill`, {});
      setFormData((prev) => ({ ...prev, ...result.suggestion }));
      setAiWarning(result.warning);
      const usage = formatUsageWithCost(result.usage, costDisplay);
      setAiUsageLine(usage ? `${result.modelId ?? "AI"}: ${usage}` : null);
      void qc.invalidateQueries({ queryKey: ["highlights", studyId, reviewId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : "AI pre-fill failed.");
    } finally {
      setAiLoading(false);
    }
  };

  const updateField = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateTopicValue = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      customTopics: {
        ...((prev["customTopics"] as Record<string, string | null>) ?? {}),
        [key]: value || null,
      },
    }));
  };

  const flash = useCallback((setter: (v: string | null) => void, id: string) => {
    setter(id);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => {
      setActiveHighlightId(null);
      setActiveFieldKey(null);
    }, 2200);
  }, []);

  // Scroll the owning pane directly: scrollIntoView affects every scrollable
  // ancestor, and smooth behavior is silently dropped in some environments.
  // Instant scroll + the flash effect is the reliable combination.
  const scrollIntoPane = (el: HTMLElement) => {
    const pane = el.closest(".overflow-y-auto") as HTMLElement | null;
    if (!pane) return;
    const target =
      el.getBoundingClientRect().top -
      pane.getBoundingClientRect().top +
      pane.scrollTop -
      pane.clientHeight / 2;
    pane.scrollTo({ top: Math.max(0, target) });
  };

  const jumpToHighlight = useCallback(
    (h: Highlight) => {
      // Evidence from the image gallery has no passage to jump to: open the image.
      if (h.imageId) {
        setLeftTab("images");
        setOpenImageId(h.imageId);
        return;
      }
      setLeftTab("text");
      const el = highlightElRefs.current.get(h.id);
      if (el) {
        scrollIntoPane(el);
        flash(setActiveHighlightId, h.id);
      }
    },
    [flash]
  );

  const jumpToField = useCallback(
    (fieldKey: string) => {
      const el = fieldElRefs.current.get(fieldKey);
      if (el) {
        scrollIntoPane(el);
        flash(setActiveFieldKey, fieldKey);
      }
    },
    [flash]
  );

  const registerField = useCallback((key: string, el: HTMLElement | null) => {
    if (el) fieldElRefs.current.set(key, el);
    else fieldElRefs.current.delete(key);
  }, []);

  const registerHighlightEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) highlightElRefs.current.set(id, el);
    else highlightElRefs.current.delete(id);
  }, []);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const isConfirmed = extractionData?.extraction?.isConfirmed ?? false;
  const isAiSuggested = extractionData?.extraction?.isAiSuggested ?? false;
  const paperText = paperData?.text ?? "";
  const paperSource = paperData?.source ?? "none";

  const provenanceValue: ProvenanceContextValue = {
    byField,
    jumpToHighlight,
    registerField,
    activeFieldKey,
  };

  return (
    <div className="h-screen flex flex-col">
      {/* ── Header bar ── */}
      <div className="border-b bg-card px-5 py-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg font-bold">Data Extraction</h1>
            {isConfirmed && (
              <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
              </span>
            )}
            {isAiSuggested && !isConfirmed && (
              <span className="flex items-center gap-1 text-amber-600 text-xs font-medium">
                <Bot className="h-3.5 w-3.5" /> AI pre-fill: review all fields before confirming
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xl">
            {review?.name}: {studyData?.study.title}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            onClick={() => guard(() => navigate(`/risk-of-bias/${studyId ?? ""}/${reviewId ?? ""}`))}
            className="flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs hover:bg-accent"
            title="Assess methodological quality / risk of bias for this study"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Quality appraisal
          </button>
          <button
            onClick={handleAiPrefill}
            disabled={aiLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary rounded-md text-xs hover:bg-primary/5 disabled:opacity-50"
          >
            {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
            AI pre-fill
          </button>
          <CostEstimate path={`/extraction/${studyId ?? ""}/${reviewId ?? ""}/ai-prefill/estimate`} compact />
          <SaveControls
            dirty={dirty}
            saving={saving}
            savedThisVisit={savedThisVisit}
            confirmed={isConfirmed}
            confirmLabel="Confirm extraction"
            confirmedLabel="Extraction confirmed"
            unconfirmBody="The extraction stays saved as a draft and can be edited and confirmed again. This change is recorded in the audit log."
            onSave={() => void handleSave(false)}
            onConfirm={() => void handleSave(true)}
            onUnconfirm={() => void handleUnconfirm()}
          />
          {saveError && <p className="w-full text-xs text-destructive text-right">{saveError}</p>}
          {leaveDialog}
        </div>
      </div>

      {aiWarning && (
        <div className="px-5 py-2 border-b border-amber-200 bg-amber-50 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          {aiWarning}
          {aiUsageLine && <span className="ml-auto whitespace-nowrap text-amber-700/80">{aiUsageLine}</span>}
        </div>
      )}

      {/* ── Side-by-side workspace ── */}
      <ProvenanceContext.Provider value={provenanceValue}>
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2">
          {/* Left: paper text, or the images pulled out of the PDF */}
          <div className="flex flex-col min-h-0 bg-card">
            <div className="flex border-b text-xs flex-shrink-0">
              <button
                onClick={() => setLeftTab("text")}
                className={`flex items-center gap-1.5 px-4 py-2 border-b-2 -mb-px ${leftTab === "text" ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                <FileText className="h-3.5 w-3.5" /> Paper text
              </button>
              <button
                onClick={() => setLeftTab("images")}
                className={`flex items-center gap-1.5 px-4 py-2 border-b-2 -mb-px ${leftTab === "images" ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                <ImageIcon className="h-3.5 w-3.5" /> Images
              </button>
              <button
                onClick={() => setLeftTab("tables")}
                className={`flex items-center gap-1.5 px-4 py-2 border-b-2 -mb-px ${leftTab === "tables" ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                <Table2 className="h-3.5 w-3.5" /> Tables
                {paperTables.length > 0 && <span className="text-[10px] text-muted-foreground">({paperTables.length})</span>}
              </button>
            </div>
            {leftTab === "text" ? (
              <PaperTextPane
                text={paperText}
                source={paperSource}
                highlights={highlights}
                activeHighlightId={activeHighlightId}
                registerHighlightEl={registerHighlightEl}
                onHighlightClick={(h) => {
                  if (h.fieldKey) jumpToField(h.fieldKey);
                }}
                fieldOptions={buildFieldOptions(review?.type, topics)}
                onCreateHighlight={(payload) => createHighlightMutation.mutate(payload)}
                creating={createHighlightMutation.isPending}
                tableMarks={tableMarks}
                onShowTable={(label) => {
                  setLeftTab("tables");
                  setTableFocus({ label, at: Date.now() });
                }}
              />
            ) : leftTab === "tables" ? (
              <div className="flex-1 overflow-y-auto min-h-0">
                {studyId && <PaperTables studyId={studyId} focus={tableFocus} />}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto min-h-0">
                {studyId && reviewId && (
                  <ImageGallery
                    studyId={studyId}
                    reviewId={reviewId}
                    fieldLabel={(key) => fieldLabel(key, review?.type, topics)}
                    currentValue={(key) =>
                      topics.some((t) => t.key === key)
                        ? ((formData["customTopics"] as Record<string, unknown> | undefined)?.[key] ?? null)
                        : formData[key] ?? null
                    }
                    onApply={(key, value) => {
                      if (topics.some((t) => t.key === key)) updateTopicValue(key, String(value));
                      else updateField(key, value);
                    }}
                    openImageId={openImageId}
                    onOpenImage={setOpenImageId}
                  />
                )}
              </div>
            )}
          </div>

          {/* Right: extraction form */}
          <div className="overflow-y-auto border-l min-h-0">
            <div className="p-5 space-y-5">
              <Section title="Population">
                <Field label="Sample size" fieldKey="sampleSize">
                  <input
                    type="number"
                    value={String(formData["sampleSize"] ?? "")}
                    onChange={(e) => updateField("sampleSize", e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="field-input"
                  />
                </Field>
                <Field label="Country" fieldKey="country">
                  <input
                    type="text"
                    value={String(formData["country"] ?? "")}
                    onChange={(e) => updateField("country", e.target.value || null)}
                    className="field-input"
                  />
                </Field>
                <Field label="Diagnoses" fieldKey="diagnoses">
                  <MultiSelect
                    options={["STROKE", "TBI", "BRAIN_TUMOUR", "MIXED_ABI", "OTHER"]}
                    value={(formData["diagnoses"] as string[]) ?? []}
                    onChange={(v) => updateField("diagnoses", v)}
                  />
                </Field>
                <Field label="Participant type" fieldKey="participantType">
                  <Select
                    options={["PATIENTS", "HEALTHY_CONTROLS", "BOTH"]}
                    value={String(formData["participantType"] ?? "")}
                    onChange={(v) => updateField("participantType", v)}
                  />
                </Field>
                <Field label="Study design" fieldKey="studyDesign">
                  <Select
                    options={["RCT", "QUASI_EXPERIMENTAL", "CASE_SERIES", "SINGLE_CASE", "TECHNICAL", "OTHER"]}
                    value={String(formData["studyDesign"] ?? "")}
                    onChange={(v) => updateField("studyDesign", v)}
                  />
                </Field>
              </Section>

              <Section title="XR System">
                <Field label="XR modality" fieldKey="xrModality">
                  <Select
                    options={["HMD_VR", "HMD_MR", "MOBILE_AR", "OTHER", "NOT_SPECIFIED"]}
                    value={String(formData["xrModality"] ?? "")}
                    onChange={(v) => updateField("xrModality", v)}
                  />
                </Field>
                <Field label="Device name" fieldKey="deviceName">
                  <input
                    type="text"
                    value={String(formData["deviceName"] ?? "")}
                    onChange={(e) => updateField("deviceName", e.target.value || null)}
                    placeholder="e.g. Meta Quest 3, HoloLens 2"
                    className="field-input"
                  />
                </Field>
                <Field label="Manufacturer" fieldKey="deviceManufacturer">
                  <input
                    type="text"
                    value={String(formData["deviceManufacturer"] ?? "")}
                    onChange={(e) => updateField("deviceManufacturer", e.target.value || null)}
                    className="field-input"
                  />
                </Field>
                <Field label="Delivery mode" fieldKey="deliveryMode">
                  <Select
                    options={["STANDALONE", "TETHERED", "NOT_SPECIFIED"]}
                    value={String(formData["deliveryMode"] ?? "")}
                    onChange={(v) => updateField("deliveryMode", v)}
                  />
                </Field>
                <Field label="Eye-tracking integrated" fieldKey="eyeTrackingIntegrated">
                  <Select
                    options={["", "true", "false"]}
                    labels={["(not specified)", "Yes", "No"]}
                    value={formData["eyeTrackingIntegrated"] == null ? "" : String(formData["eyeTrackingIntegrated"])}
                    onChange={(v) => updateField("eyeTrackingIntegrated", v === "" ? null : v === "true")}
                  />
                </Field>
                <Field label="Neglect terminology used by authors" fieldKey="neglectTerminologyUsed">
                  <input
                    type="text"
                    value={String(formData["neglectTerminologyUsed"] ?? "")}
                    onChange={(e) => updateField("neglectTerminologyUsed", e.target.value || null)}
                    placeholder='e.g. "spatial neglect", "hemispatial neglect"'
                    className="field-input"
                  />
                </Field>
                <Field label="Funding source" fieldKey="fundingSource">
                  <input
                    type="text"
                    value={String(formData["fundingSource"] ?? "")}
                    onChange={(e) => updateField("fundingSource", e.target.value || null)}
                    className="field-input"
                  />
                </Field>
              </Section>

              {/* Review-specific fields */}
              {review?.type === "ASSESSMENT" && <AssessmentFields data={formData} update={updateField} />}
              {review?.type === "TREATMENT" && <TreatmentFields data={formData} update={updateField} />}
              {review?.type === "TECHNICAL" && <TechnicalFields data={formData} update={updateField} />}

              {/* Custom subtopics */}
              <TopicsSection
                topics={topics}
                values={(formData["customTopics"] as Record<string, string | null>) ?? {}}
                onValueChange={updateTopicValue}
                onCreate={(label, description) => createTopicMutation.mutate({ label, description })}
                onDelete={(topicId) => deleteTopicMutation.mutate(topicId)}
                creating={createTopicMutation.isPending}
              />

              {/* Highlights & quotes list */}
              <HighlightsSection
                highlights={highlights}
                reviewType={review?.type}
                topics={topics}
                onJump={jumpToHighlight}
                onDelete={(id) => deleteHighlightMutation.mutate(id)}
              />

              {/* Notes */}
              <Section title="Extractor notes">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Optional notes for your co-extractor or coordinator…"
                />
              </Section>
            </div>
          </div>
        </div>
      </ProvenanceContext.Provider>
    </div>
  );
}

// ─── Paper text pane ──────────────────────────────────────────────────────────

interface FieldOption {
  key: string;
  label: string;
}

function buildFieldOptions(reviewType: string | undefined, topics: Topic[]): FieldOption[] {
  const options: FieldOption[] = Object.entries(SHARED_FIELD_LABELS).map(([key, label]) => ({
    key,
    label,
  }));
  if (reviewType && TYPE_FIELD_LABELS[reviewType]) {
    for (const [key, label] of Object.entries(TYPE_FIELD_LABELS[reviewType])) {
      options.push({ key, label });
    }
  }
  for (const t of topics) {
    options.push({ key: t.key, label: `Topic: ${t.label}` });
  }
  return options;
}

interface SelectionState {
  start: number;
  end: number;
  quote: string;
  x: number;
  y: number;
}

interface PaperTextPaneProps {
  text: string;
  source: PaperSource;
  highlights: Highlight[];
  activeHighlightId: string | null;
  registerHighlightEl: (id: string, el: HTMLElement | null) => void;
  onHighlightClick: (h: Highlight) => void;
  fieldOptions: FieldOption[];
  onCreateHighlight: (payload: {
    fieldKey: string | null;
    quote: string;
    startOffset: number;
    endOffset: number;
    note: string | null;
    purpose: HighlightPurpose;
  }) => void;
  creating: boolean;
  /** Where each rebuilt table's caption starts in the text. */
  tableMarks: Array<{ offset: number; label: string }>;
  onShowTable: (label: string) => void;
}

interface Segment {
  start: number;
  end: number;
  covering: Highlight[];
}

function segmentText(text: string, highlights: Highlight[], extraPoints: number[] = []): Segment[] {
  const anchored = highlights.filter(
    (h) =>
      h.startOffset != null &&
      h.endOffset != null &&
      h.startOffset < h.endOffset &&
      h.endOffset <= text.length
  );
  const points = new Set<number>([0, text.length, ...extraPoints.filter((p) => p > 0 && p < text.length)]);
  for (const h of anchored) {
    points.add(h.startOffset as number);
    points.add(h.endOffset as number);
  }
  const sorted = [...points].sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];
    const covering = anchored.filter(
      (h) => (h.startOffset as number) <= start && (h.endOffset as number) >= end
    );
    segments.push({ start, end, covering });
  }
  return segments;
}

function PaperTextPane({
  text,
  source,
  highlights,
  activeHighlightId,
  registerHighlightEl,
  onHighlightClick,
  fieldOptions,
  onCreateHighlight,
  creating,
  tableMarks,
  onShowTable,
}: PaperTextPaneProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [selFieldKey, setSelFieldKey] = useState<string>("");
  const [selPurpose, setSelPurpose] = useState<HighlightPurpose>("EVIDENCE");
  const [selNote, setSelNote] = useState("");

  const segments = useMemo(
    () => segmentText(text, highlights, tableMarks.map((m) => m.offset)),
    [text, highlights, tableMarks]
  );
  const marksAt = useMemo(() => new Map(tableMarks.map((m) => [m.offset, m.label])), [tableMarks]);

  // A table in the plain text is a jumble; where one starts, offer the rebuilt
  // version. Not selectable and outside any [data-start] span, so highlight
  // offsets are unaffected.
  const tableMark = (offset: number) => {
    const label = marksAt.get(offset);
    if (!label) return null;
    return (
      <button
        key={`table-${offset}`}
        type="button"
        contentEditable={false}
        onClick={() => onShowTable(label)}
        className="select-none inline-flex items-center gap-1 mr-1.5 px-2 py-0.5 rounded-full border border-primary/40 bg-primary/10 text-primary text-[11px] font-sans font-medium align-middle hover:bg-primary/20"
        title="The text of a table loses its layout. Open this one rebuilt as a table."
      >
        <Table2 className="h-3 w-3" /> Show {label} as a table
      </button>
    );
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // Clicks inside the popover must not re-evaluate the selection
    if ((e.target as HTMLElement).closest("[data-selection-popover]")) return;

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setSelection(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const globalOffset = (node: Node, offset: number): number | null => {
      const textNode = node.nodeType === Node.TEXT_NODE ? node : null;
      if (!textNode) return null;
      const span = (textNode.parentElement ?? undefined)?.closest?.("[data-start]") as
        | HTMLElement
        | undefined;
      if (!span || !contentRef.current?.contains(span)) return null;
      return parseInt(span.dataset["start"] ?? "0", 10) + offset;
    };
    const start = globalOffset(range.startContainer, range.startOffset);
    const end = globalOffset(range.endContainer, range.endOffset);
    if (start == null || end == null || end <= start) {
      setSelection(null);
      return;
    }
    const quote = text.slice(start, end);
    if (!quote.trim() || quote.length > 5000) {
      setSelection(null);
      return;
    }

    const rect = range.getBoundingClientRect();
    const contentRect = contentRef.current?.getBoundingClientRect();
    if (!contentRect) return;
    setSelection({
      start,
      end,
      quote,
      x: Math.max(8, Math.min(rect.left - contentRect.left, contentRect.width - 330)),
      y: rect.bottom - contentRect.top + 8,
    });
    setSelFieldKey("");
    setSelPurpose("EVIDENCE");
    setSelNote("");
  };

  const saveSelection = () => {
    if (!selection) return;
    onCreateHighlight({
      fieldKey: selFieldKey || null,
      quote: selection.quote,
      startOffset: selection.start,
      endOffset: selection.end,
      note: selNote.trim() || null,
      purpose: selPurpose,
    });
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const sourceBadge =
    source === "pdf" ? (
      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium">
        Full text (PDF)
      </span>
    ) : source === "abstract" ? (
      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-medium">
        Abstract only
      </span>
    ) : source === "scanned_pdf" ? (
      <span
        className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium"
        title="This PDF is a scan with no text layer, so the text cannot be read. The abstract is shown, and the AI also works from the abstract. The scanned pages appear under Images."
      >
        Scanned PDF: abstract shown
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-medium">
        No text available
      </span>
    );

  return (
    <div className="flex-1 overflow-y-auto min-h-0 bg-card">
      <div className="sticky top-0 z-10 bg-card border-b px-5 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <FileText className="h-4 w-4 text-muted-foreground" /> Paper
          {sourceBadge}
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Highlighter className="h-3 w-3" /> Select text to create a highlight
        </p>
      </div>

      {text ? (
        <div
          ref={contentRef}
          className="relative p-5 whitespace-pre-wrap text-[13px] leading-relaxed font-serif"
          onMouseUp={handleMouseUp}
        >
          {segments.map((seg) => {
            const slice = text.slice(seg.start, seg.end);
            const mark = tableMark(seg.start);
            if (seg.covering.length === 0) {
              return (
                <Fragment key={seg.start}>
                  {mark}
                  <span data-start={seg.start}>{slice}</span>
                </Fragment>
              );
            }
            const first = seg.covering[0];
            const isAi = seg.covering.some((h) => h.isAiGenerated);
            const isManual = seg.covering.some((h) => !h.isAiGenerated);
            const isActive = seg.covering.some((h) => h.id === activeHighlightId);
            const cls = [
              "cursor-pointer transition-colors rounded-[2px]",
              isManual
                ? "bg-brand-magenta/15 border-b-2 border-brand-magenta/60"
                : "bg-primary/15 border-b-2 border-primary/60",
              isAi && isManual ? "bg-primary/25" : "",
              isActive ? "bg-yellow-200 ring-2 ring-yellow-400" : "",
            ].join(" ");
            const title = seg.covering
              .map(
                (h) =>
                  `${h.isAiGenerated ? "AI" : h.createdBy?.name ?? "Reviewer"}${h.fieldKey ? ` → ${h.fieldKey}` : ""}${h.note ? `: ${h.note}` : ""}`
              )
              .join("\n");
            return (
              <Fragment key={seg.start}>
                {mark}
                <span
                  data-start={seg.start}
                  ref={(el) => {
                    for (const h of seg.covering) {
                      if (h.startOffset === seg.start) registerHighlightEl(h.id, el);
                    }
                  }}
                  className={cls}
                  title={title}
                  onClick={() => onHighlightClick(first)}
                >
                  {slice}
                </span>
              </Fragment>
            );
          })}

          {/* Selection popover */}
          {selection && (
            <div
              data-selection-popover
              className="absolute z-20 w-80 border rounded-lg bg-popover text-popover-foreground shadow-lg p-3 space-y-2"
              style={{ left: selection.x, top: selection.y }}
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold flex items-center gap-1.5">
                  <Highlighter className="h-3.5 w-3.5 text-brand-magenta" /> New highlight
                </p>
                <button
                  onClick={() => setSelection(null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground border-l-2 border-brand-magenta/50 pl-2 max-h-16 overflow-hidden">
                {selection.quote.length > 180 ? selection.quote.slice(0, 180) + "…" : selection.quote}
              </p>
              <select
                value={selFieldKey}
                onChange={(e) => setSelFieldKey(e.target.value)}
                className="w-full border rounded-md px-2 py-1.5 text-xs bg-background"
              >
                <option value="">No linked field (free annotation)</option>
                {fieldOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                value={selPurpose}
                onChange={(e) => setSelPurpose(e.target.value as HighlightPurpose)}
                className="w-full border rounded-md px-2 py-1.5 text-xs bg-background"
              >
                <option value="EVIDENCE">Evidence for a data field</option>
                <option value="QUOTE_FOR_REVIEW">Quote to use in the final review</option>
                <option value="NOTE">General note</option>
              </select>
              <input
                type="text"
                value={selNote}
                onChange={(e) => setSelNote(e.target.value)}
                placeholder="Optional note…"
                className="w-full border rounded-md px-2 py-1.5 text-xs bg-background"
              />
              <button
                onClick={saveSelection}
                disabled={creating}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-brand-magenta text-white rounded-md text-xs font-medium hover:opacity-90 disabled:opacity-50"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Save highlight
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="p-8 text-center text-sm text-muted-foreground">
          <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-foreground mb-1">No paper text available</p>
          <p className="text-xs">
            Upload a PDF for this study in the full-text screening step to enable the
            side-by-side extraction view.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Custom topics section ────────────────────────────────────────────────────

function TopicsSection({
  topics,
  values,
  onValueChange,
  onCreate,
  onDelete,
  creating,
}: {
  topics: Topic[];
  values: Record<string, string | null>;
  onValueChange: (key: string, value: string) => void;
  onCreate: (label: string, description: string | null) => void;
  onDelete: (topicId: string) => void;
  creating: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");

  const submit = () => {
    if (!label.trim()) return;
    onCreate(label.trim(), description.trim() || null);
    setLabel("");
    setDescription("");
    setAdding(false);
  };

  return (
    <div className="border rounded-lg p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Custom subtopics
        </h3>
        <button
          onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Plus className="h-3.5 w-3.5" /> Add subtopic
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">
        Reviewer-defined topics of interest (e.g. recorded variables for WG4). The AI will
        extract these on its next pre-fill run and must cite where in the paper each answer
        comes from.
      </p>

      {adding && (
        <div className="border rounded-md p-3 mb-4 space-y-2 bg-muted/40">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Topic label, e.g. Variables recorded"
            className="w-full border rounded-md px-2 py-1.5 text-xs bg-background"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Guidance: what should the AI and human extractors look for?"
            rows={2}
            className="w-full border rounded-md px-2 py-1.5 text-xs bg-background"
          />
          <div className="flex gap-2">
            <button
              onClick={submit}
              disabled={creating || !label.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Create topic
            </button>
            <button
              onClick={() => setAdding(false)}
              className="px-3 py-1.5 border rounded-md text-xs hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {topics.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground italic">No custom subtopics defined yet.</p>
      )}

      <div className="space-y-3">
        {topics.map((t) => (
          <Field key={t.id} label={t.label} fieldKey={t.key}>
            <div className="flex gap-2 items-start">
              <textarea
                value={values[t.key] ?? ""}
                onChange={(e) => onValueChange(t.key, e.target.value)}
                rows={2}
                placeholder={t.description ?? "Extracted value…"}
                title={t.description ?? undefined}
                className="flex-1 border rounded-md px-2 py-1.5 text-xs bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                onClick={() => {
                  if (confirm(`Delete subtopic "${t.label}"?`)) onDelete(t.id);
                }}
                className="text-muted-foreground hover:text-destructive p-1"
                title="Delete subtopic"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </Field>
        ))}
      </div>
    </div>
  );
}

// ─── Highlights list section ──────────────────────────────────────────────────

const PURPOSE_LABELS: Record<HighlightPurpose, string> = {
  EVIDENCE: "Evidence",
  QUOTE_FOR_REVIEW: "For final review",
  NOTE: "Note",
};

function HighlightsSection({
  highlights,
  reviewType,
  topics,
  onJump,
  onDelete,
}: {
  highlights: Highlight[];
  reviewType: string | undefined;
  topics: Topic[];
  onJump: (h: Highlight) => void;
  onDelete: (id: string) => void;
}) {
  if (highlights.length === 0) return null;
  return (
    <div className="border rounded-lg p-5">
      <h3 className="text-sm font-semibold mb-1 text-muted-foreground uppercase tracking-wide">
        Highlights and quotes
      </h3>
      <p className="text-[11px] text-muted-foreground mb-4">
        Every AI value and reviewer highlight is anchored to the paper text. Click the
        crosshair to jump to the source passage.
      </p>
      <div className="space-y-2">
        {highlights.map((h) => (
          <div key={h.id} className="border rounded-md p-2.5 text-xs flex items-start gap-2">
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                h.isAiGenerated
                  ? "bg-primary/15 text-primary"
                  : "bg-brand-magenta/15 text-brand-magenta"
              }`}
            >
              {h.isAiGenerated ? "AI" : h.createdBy?.name ?? "Reviewer"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                {h.fieldKey && (
                  <span className="font-semibold">{fieldLabel(h.fieldKey, reviewType, topics)}</span>
                )}
                <span className="text-muted-foreground text-[10px]">{PURPOSE_LABELS[h.purpose]}</span>
                {h.sourceKind === "FIGURE" ? (
                  <span className="text-primary text-[10px]">from an image, page {h.sourcePage ?? "?"}</span>
                ) : h.startOffset == null && (
                  <span className="text-amber-600 text-[10px]" title="This quote could not be located verbatim in the extracted text">
                    unanchored
                  </span>
                )}
              </div>
              <p className="text-muted-foreground line-clamp-2 italic">"{h.quote}"</p>
              {h.note && <p className="mt-0.5 text-foreground">{h.note}</p>}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {(h.startOffset != null || h.imageId) && (
                <button
                  onClick={() => onJump(h)}
                  className="p-1 text-muted-foreground hover:text-primary"
                  title={h.imageId ? "Open the image" : "Jump to source passage in the paper"}
                >
                  <Crosshair className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => onDelete(h.id)}
                className="p-1 text-muted-foreground hover:text-destructive"
                title="Delete highlight"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Review-specific field groups ────────────────────────────────────────────

function AssessmentFields({ data, update }: FieldGroupProps) {
  return (
    <Section title="Assessment-specific fields">
      <Field label="Assessment type" fieldKey="assessmentType">
        <Select
          options={["", "NEGLECT_TEST_REPLICATED", "NOVEL_TASK", "ADL_SIMULATION"]}
          value={String(data["assessmentType"] ?? "")}
          onChange={(v) => update("assessmentType", v || null)}
        />
      </Field>
      <Field label="Comparison condition" fieldKey="comparisonCondition">
        <Select
          options={["", "CONVENTIONAL_LINE_BISECTION", "CONVENTIONAL_CANCELLATION", "CONVENTIONAL_FULL_BATTERY", "CONVENTIONAL_OTHER", "HEALTHY_CONTROLS_SAME_VR", "NONE", "OTHER"]}
          value={String(data["comparisonCondition"] ?? "")}
          onChange={(v) => update("comparisonCondition", v || null)}
        />
      </Field>
      <Field label="Reliability metrics (free text)" fieldKey="reliabilityMetrics">
        <input type="text" value={String(data["reliabilityMetrics"] ?? "")} onChange={(e) => update("reliabilityMetrics", e.target.value || null)} className="field-input" />
      </Field>
      <Field label="Performance measures" fieldKey="performanceMeasures">
        <input type="text" value={String(data["performanceMeasures"] ?? "")} onChange={(e) => update("performanceMeasures", e.target.value || null)} className="field-input" />
      </Field>
      <Field label="Dropout rate (0–1)" fieldKey="dropoutRate">
        <input type="number" step="0.01" min="0" max="1" value={String(data["dropoutRate"] ?? "")} onChange={(e) => update("dropoutRate", e.target.value ? parseFloat(e.target.value) : null)} className="field-input" />
      </Field>
      <Field label="Adverse events reported" fieldKey="adverseEventsReported">
        <Select options={["", "true", "false"]} labels={["(not specified)", "Yes", "No"]} value={data["adverseEventsReported"] == null ? "" : String(data["adverseEventsReported"])} onChange={(v) => update("adverseEventsReported", v === "" ? null : v === "true")} />
      </Field>
    </Section>
  );
}

function TreatmentFields({ data, update }: FieldGroupProps) {
  return (
    <Section title="Treatment-specific fields">
      <Field label="Intervention description" fieldKey="interventionDescription">
        <textarea value={String(data["interventionDescription"] ?? "")} onChange={(e) => update("interventionDescription", e.target.value || null)} rows={2} className="w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
      </Field>
      <Field label="Sessions per week" fieldKey="sessionsPerWeek">
        <input type="number" value={String(data["sessionsPerWeek"] ?? "")} onChange={(e) => update("sessionsPerWeek", e.target.value ? parseInt(e.target.value, 10) : null)} className="field-input" />
      </Field>
      <Field label="Total sessions" fieldKey="totalSessions">
        <input type="number" value={String(data["totalSessions"] ?? "")} onChange={(e) => update("totalSessions", e.target.value ? parseInt(e.target.value, 10) : null)} className="field-input" />
      </Field>
      <Field label="Primary outcome measure" fieldKey="primaryOutcomeMeasure">
        <input type="text" value={String(data["primaryOutcomeMeasure"] ?? "")} onChange={(e) => update("primaryOutcomeMeasure", e.target.value || null)} className="field-input" />
      </Field>
      <Field label="Effect size" fieldKey="effectSize">
        <input type="text" value={String(data["effectSize"] ?? "")} onChange={(e) => update("effectSize", e.target.value || null)} placeholder="e.g. d = 0.74" className="field-input" />
      </Field>
      <Field label="p-value" fieldKey="pValue">
        <input type="text" value={String(data["pValue"] ?? "")} onChange={(e) => update("pValue", e.target.value || null)} placeholder="e.g. p < 0.05" className="field-input" />
      </Field>
      <Field label="Risk of bias tool" fieldKey="robAssessmentType">
        <Select options={["", "ROB2", "ROBINS_I"]} value={String(data["robAssessmentType"] ?? "")} onChange={(v) => update("robAssessmentType", v || null)} />
      </Field>
      <Field label="Overall RoB judgement" fieldKey="robOverallJudgement">
        <Select options={["", "LOW", "SOME_CONCERNS", "HIGH"]} value={String(data["robOverallJudgement"] ?? "")} onChange={(v) => update("robOverallJudgement", v || null)} />
      </Field>
    </Section>
  );
}

function TechnicalFields({ data, update }: FieldGroupProps) {
  return (
    <Section title="Technical/design-feature fields">
      <Field label="Software type" fieldKey="softwareType">
        <Select options={["", "CUSTOM_BUILT", "COMMERCIAL", "OPEN_SOURCE", "NOT_SPECIFIED"]} value={String(data["softwareType"] ?? "")} onChange={(v) => update("softwareType", v || null)} />
      </Field>
      <Field label="Software name" fieldKey="softwareName">
        <input type="text" value={String(data["softwareName"] ?? "")} onChange={(e) => update("softwareName", e.target.value || null)} className="field-input" />
      </Field>
      <Field label="Interaction modalities" fieldKey="interactionModalities">
        <MultiSelect options={["GAZE", "CONTROLLER", "GESTURE", "TOUCHSCREEN", "OTHER"]} value={(data["interactionModalities"] as string[]) ?? []} onChange={(v) => update("interactionModalities", v)} />
      </Field>
      <Field label="Feedback types" fieldKey="feedbackTypes">
        <MultiSelect options={["VISUAL", "AUDITORY", "HAPTIC", "MULTIMODAL"]} value={(data["feedbackTypes"] as string[]) ?? []} onChange={(v) => update("feedbackTypes", v)} />
      </Field>
      <Field label="Outcome measures captured" fieldKey="outcomeMeasuresCaptured">
        <input type="text" value={String(data["outcomeMeasuresCaptured"] ?? "")} onChange={(e) => update("outcomeMeasuresCaptured", e.target.value || null)} className="field-input" />
      </Field>
    </Section>
  );
}

// ─── Reusable form primitives ─────────────────────────────────────────────────

interface FieldGroupProps {
  data: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-lg p-5">
      <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ProvenanceBadge({ highlights, onJump }: { highlights: Highlight[]; onJump: (h: Highlight) => void }) {
  // A text passage to jump to, or failing that an image to open.
  const anchored = highlights.find((h) => h.startOffset != null) ?? highlights.find((h) => h.imageId);
  return (
    <span className="relative group inline-flex ml-1.5 align-middle">
      <button
        type="button"
        onClick={() => anchored && onJump(anchored)}
        className={`inline-flex items-center justify-center h-4 w-4 rounded-full border text-[9px] ${
          highlights.some((h) => h.isAiGenerated)
            ? "border-primary/50 text-primary bg-primary/10"
            : "border-brand-magenta/50 text-brand-magenta bg-brand-magenta/10"
        } ${anchored ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}`}
        title={anchored?.imageId ? "Click to open the image this came from" : anchored ? "Click to jump to the source passage" : "Source quote (not anchored in text)"}
      >
        <Quote className="h-2.5 w-2.5" />
      </button>
      {/* Hover popover with the supporting quote(s) */}
      <span className="hidden group-hover:block absolute left-0 top-full mt-1 z-30 w-72 border rounded-lg bg-popover text-popover-foreground shadow-lg p-3 space-y-2">
        {highlights.map((h) => (
          <span key={h.id} className="block">
            <span
              className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium mb-1 ${
                h.isAiGenerated ? "bg-primary/15 text-primary" : "bg-brand-magenta/15 text-brand-magenta"
              }`}
            >
              {h.isAiGenerated ? (h.sourceKind === "FIGURE" ? `AI, from an image on page ${h.sourcePage ?? "?"}` : "AI evidence") : h.createdBy?.name ?? "Reviewer"}
            </span>
            <span className="block text-[11px] text-muted-foreground italic border-l-2 border-border pl-2">
              "{h.quote.length > 220 ? h.quote.slice(0, 220) + "…" : h.quote}"
            </span>
            {h.note && <span className="block text-[11px] mt-0.5">{h.note}</span>}
          </span>
        ))}
      </span>
    </span>
  );
}

function Field({
  label,
  fieldKey,
  children,
}: {
  label: string;
  fieldKey?: string;
  children: React.ReactNode;
}) {
  const ctx = useContext(ProvenanceContext);
  const provenance = fieldKey && ctx ? ctx.byField[fieldKey] ?? [] : [];
  const isActive = fieldKey != null && ctx?.activeFieldKey === fieldKey;

  return (
    <div
      ref={(el) => {
        if (fieldKey && ctx) ctx.registerField(fieldKey, el);
      }}
      className={`grid grid-cols-3 gap-3 items-start rounded-md transition-colors ${
        isActive ? "bg-yellow-100 ring-2 ring-yellow-300 p-1 -m-1" : ""
      }`}
    >
      <label className="text-sm font-medium pt-2 col-span-1">
        {label}
        {provenance.length > 0 && ctx && (
          <ProvenanceBadge highlights={provenance} onJump={ctx.jumpToHighlight} />
        )}
      </label>
      <div className="col-span-2">{children}</div>
    </div>
  );
}

function Select({
  options,
  labels,
  value,
  onChange,
}: {
  options: string[];
  labels?: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {options.map((opt, i) => (
        <option key={opt} value={opt}>
          {(labels?.[i] ?? opt) || "Select..."}
        </option>
      ))}
    </select>
  );
}

function MultiSelect({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (opt: string) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            value.includes(opt)
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background border-border hover:bg-accent"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}
